// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Plus, Edit2, Trash2, X, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { z } from 'zod';

interface VideoPackage {
  id: string;
  vendor_id: string;
  package_type: string;
  price_cents: number;
  description?: string;
  features?: string[];
  is_active: boolean;
  created_at: string;
}

interface PackageManagementModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  vendorId: string;
  onPackageUpdate?: () => void;
}

const packageTypeColors = {
  silver: 'bg-gray-100 text-gray-800 border-gray-300',
  gold: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  platinum: 'bg-purple-100 text-purple-800 border-purple-300',
};

const packageSchema = z.object({
  package_type: z.string().min(1, 'Package type is required'),
  price_cents: z.number().min(1, 'Price must be greater than 0'),
  description: z.string().min(1, 'Description is required'),
  features: z.array(z.string()).min(1, 'At least one feature is required'),
  is_active: z.boolean(),
});

type PackageFormData = z.infer<typeof packageSchema>;

const PackageManagementModal: React.FC<PackageManagementModalProps> = ({
  open,
  onOpenChange,
  vendorId,
  onPackageUpdate,
}) => {
  const { user } = useAuth();
  const [packages, setPackages] = useState<VideoPackage[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingPackage, setEditingPackage] = useState<VideoPackage | null>(null);
  
  const [formData, setFormData] = useState<PackageFormData>({
    package_type: 'silver',
    price_cents: 0,
    description: '',
    features: [],
    is_active: true,
  });
  
  const [newFeature, setNewFeature] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open && vendorId) {
      fetchPackages();
    }
  }, [open, vendorId]);

  const fetchPackages = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('video_packages')
        .select('*')
        .eq('vendor_id', vendorId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPackages(data || []);
    } catch (error) {
      console.error('Error fetching packages:', error);
      toast.error('Failed to load packages');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      package_type: 'silver',
      price_cents: 0,
      description: '',
      features: [],
      is_active: true,
    });
    setNewFeature('');
    setErrors({});
    setEditingPackage(null);
  };

  const handleEdit = (pkg: VideoPackage) => {
    setFormData({
      package_type: pkg.package_type || 'silver',
      price_cents: pkg.price_cents,
      description: pkg.description || '',
      features: pkg.features || [],
      is_active: pkg.is_active,
    });
    setEditingPackage(pkg);
    setShowForm(true);
  };

  const handleAddFeature = () => {
    if (newFeature.trim()) {
      setFormData(prev => ({
        ...prev,
        features: [...prev.features, newFeature.trim()]
      }));
      setNewFeature('');
    }
  };

  const handleRemoveFeature = (index: number) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const validateForm = (data: PackageFormData): boolean => {
    try {
      packageSchema.parse(data);
      setErrors({});
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        const newErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) {
            newErrors[err.path[0] as string] = err.message;
          }
        });
        setErrors(newErrors);
      }
      return false;
    }
  };

  const handleSubmit = async () => {
    if (!validateForm(formData)) return;

    setSubmitting(true);
    try {
      if (editingPackage) {
        const { error } = await supabase
          .from('video_packages')
          .update(formData)
          .eq('id', editingPackage.id);

        if (error) throw error;
        toast.success('Package updated successfully');
      } else {
        const { error } = await supabase
          .from('video_packages')
          .insert({
            ...formData,
            vendor_id: vendorId,
          });

        if (error) throw error;
        toast.success('Package created successfully');
      }

      await fetchPackages();
      onPackageUpdate?.();
      setShowForm(false);
      resetForm();
    } catch (error) {
      console.error('Error saving package:', error);
      toast.error('Failed to save package');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (pkg: VideoPackage) => {
    if (!confirm(`Are you sure you want to delete "${pkg.package_type}"?`)) return;

    try {
      const { error } = await supabase
        .from('video_packages')
        .delete()
        .eq('id', pkg.id);

      if (error) throw error;
      
      toast.success('Package deleted successfully');
      await fetchPackages();
      onPackageUpdate?.();
    } catch (error) {
      console.error('Error deleting package:', error);
      toast.error('Failed to delete package');
    }
  };

  const formatPrice = (cents: number) => {
    return (cents / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Package Management</DialogTitle>
          <DialogDescription>
            Create and manage your video service packages
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Add Package Button */}
          {!showForm && (
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">Your Packages</h3>
              <Button 
                onClick={() => {
                  resetForm();
                  setShowForm(true);
                }}
                className="flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Add Package
              </Button>
            </div>
          )}

          {/* Package Form */}
          {showForm && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>
                    {editingPackage ? 'Edit Package' : 'Create New Package'}
                  </CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowForm(false);
                      resetForm();
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Package Type/Name */}
                  <div className="space-y-2">
                    <Label htmlFor="package_type">Package Type</Label>
                    <Select
                      value={formData.package_type}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, package_type: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select package type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="silver">Silver</SelectItem>
                        <SelectItem value="gold">Gold</SelectItem>
                        <SelectItem value="platinum">Platinum</SelectItem>
                      </SelectContent>
                    </Select>
                    {errors.package_type && (
                      <p className="text-sm text-destructive">{errors.package_type}</p>
                    )}
                  </div>

                  {/* Price */}
                  <div className="space-y-2">
                    <Label htmlFor="price">Price (USD)</Label>
                    <Input
                      id="price"
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.price_cents / 100}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        price_cents: Math.round(parseFloat(e.target.value || '0') * 100)
                      }))}
                      placeholder="199.99"
                    />
                    {errors.price_cents && (
                      <p className="text-sm text-destructive">{errors.price_cents}</p>
                    )}
                  </div>

                  {/* Active Status */}
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="is_active"
                      checked={formData.is_active}
                      onCheckedChange={(checked) => 
                        setFormData(prev => ({ ...prev, is_active: checked }))
                      }
                    />
                    <Label htmlFor="is_active">Active Package</Label>
                  </div>
                </div>

                {/* Description */}
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Describe what's included in this package..."
                    rows={3}
                  />
                  {errors.description && (
                    <p className="text-sm text-destructive">{errors.description}</p>
                  )}
                </div>

                {/* Features */}
                <div className="space-y-2">
                  <Label>Features</Label>
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <Input
                        value={newFeature}
                        onChange={(e) => setNewFeature(e.target.value)}
                        placeholder="Add a feature..."
                        onKeyPress={(e) => e.key === 'Enter' && handleAddFeature()}
                      />
                      <Button type="button" onClick={handleAddFeature} size="sm">
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    
                    {formData.features.length > 0 && (
                      <div className="space-y-1">
                        {formData.features.map((feature, index) => (
                          <div key={index} className="flex items-center justify-between bg-muted p-2 rounded">
                            <span className="text-sm">{feature}</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveFeature(index)}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                    {errors.features && (
                      <p className="text-sm text-destructive">{errors.features}</p>
                    )}
                  </div>
                </div>

                <Separator />

                {/* Form Actions */}
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowForm(false);
                      resetForm();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleSubmit} disabled={submitting}>
                    {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    {editingPackage ? 'Update Package' : 'Create Package'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Packages Table */}
          {!loading && packages.length > 0 && (
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Package Name</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead>Features</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {packages.map((pkg) => (
                    <TableRow key={pkg.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{pkg.package_type}</div>
                          <div className="text-sm text-muted-foreground truncate max-w-[200px]">
                            {pkg.description}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">
                        {formatPrice(pkg.price_cents)}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {(pkg.features || []).slice(0, 2).map((feature, index) => (
                            <div key={index}>• {feature}</div>
                          ))}
                          {(pkg.features || []).length > 2 && (
                            <div className="text-muted-foreground">
                              +{(pkg.features || []).length - 2} more
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={pkg.is_active ? 'default' : 'secondary'}>
                          {pkg.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(pkg)}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(pkg)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin mr-2" />
              <span>Loading packages...</span>
            </div>
          )}

          {/* Empty State */}
          {!loading && packages.length === 0 && !showForm && (
            <div className="text-center py-8">
              <div className="text-muted-foreground mb-4">
                No packages found. Create your first package to get started.
              </div>
              <Button 
                onClick={() => {
                  resetForm();
                  setShowForm(true);
                }}
                className="flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Create Your First Package
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PackageManagementModal;